import React from "react"
import styled from "styled-components"
import devices from "styles/devices"
import { colors } from "styles/colors"

interface ProgressRingProps {
  /** 0 to 100 */
  percent: number
  /** Shown under the percentage from tablet width up, and used as the accessible name */
  caption?: string
}

// The ring is drawn in a fixed 120 unit box and scaled with css, so the stroke scales with it.
const RADIUS = 52
const STROKE_WIDTH = 12
const CIRCUMFERENCE = 2 * Math.PI * RADIUS

const Wrapper = styled.div`
  position: relative;
  width: 64px;
  height: 64px;
  flex-shrink: 0;

  @media screen and ${devices.bp_sm} {
    width: 120px;
    height: 120px;
  }

  @media screen and ${devices.bp_lg} {
    width: 168px;
    height: 168px;
  }
`

const Svg = styled.svg`
  display: block;
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
`

const Arc = styled.circle`
  stroke: ${({ theme }) => theme.colors.status.warning};
`

const Label = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.primary};
`

const Value = styled.span`
  font-weight: 600;
  font-size: 14px;
  line-height: 1;

  @media screen and ${devices.bp_sm} {
    font-size: 32px;
  }

  @media screen and ${devices.bp_lg} {
    font-size: 40px;
  }
`

const Unit = styled.span`
  margin-left: 1px;
  font-weight: 500;
  font-size: 10px;

  @media screen and ${devices.bp_sm} {
    margin-left: 2px;
    font-size: 16px;
  }

  @media screen and ${devices.bp_lg} {
    font-size: 20px;
  }
`

const Caption = styled.span`
  display: none;
  margin-top: 4px;
  font-weight: 500;
  font-size: 11px;
  line-height: 1.2;
  color: ${({ theme }) => theme.colors.text.secondary};

  @media screen and ${devices.bp_sm} {
    display: block;
  }

  @media screen and ${devices.bp_lg} {
    font-size: 13px;
  }
`

export const ProgressRing: React.FC<ProgressRingProps> = ({ percent, caption }) => {
  const value = Math.min(100, Math.max(0, Math.round(percent)))

  return (
    <Wrapper
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={caption ? `${caption}: ${value}%` : `${value}%`}
      data-testid="progress-ring"
    >
      <Svg viewBox="0 0 120 120" aria-hidden="true" focusable="false">
        <circle
          cx="60"
          cy="60"
          r={RADIUS}
          fill="none"
          stroke={colors.neutral[100]}
          strokeWidth={STROKE_WIDTH}
        />
        <Arc
          cx="60"
          cy="60"
          r={RADIUS}
          fill="none"
          strokeWidth={STROKE_WIDTH}
          strokeLinecap="round"
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={CIRCUMFERENCE * (1 - value / 100)}
        />
      </Svg>
      <Label aria-hidden="true">
        <span>
          <Value>{value}</Value>
          <Unit>%</Unit>
        </span>
        {caption && <Caption>{caption}</Caption>}
      </Label>
    </Wrapper>
  )
}
